import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { PATHS } from 'router/config';
import { IGroupOverview } from 'core/hooks';

import * as S from './AccountManagerDashboard.styled';

const fmtDebt = (amount: number, currency: string): string =>
  amount > 0
    ? `${Math.round(amount).toLocaleString('ru-RU')} ${currency}`
    : '—';

type SortKey =
  | 'name'
  | 'students'
  | 'debt'
  | 'debtors'
  | 'unanswered'
  | 'tasks';

/* Explicit per-column widths + `table-layout: fixed` keep header and body
 * cells aligned regardless of content (the old auto-layout drifted). */
const COLUMNS: Array<{
  key: SortKey;
  label: string;
  align: 'left' | 'right';
  width: string;
}> = [
  { key: 'name', label: 'group', align: 'left', width: '28%' },
  { key: 'students', label: 'students', align: 'right', width: '10%' },
  { key: 'debt', label: 'debt', align: 'right', width: '14%' },
  { key: 'debtors', label: 'debtors', align: 'right', width: '10%' },
  { key: 'unanswered', label: 'unanswered', align: 'right', width: '12%' },
  { key: 'tasks', label: 'tasks', align: 'right', width: '10%' },
];

const sortValue = (g: IGroupOverview, key: SortKey): number | string => {
  switch (key) {
    case 'students':
      return g.studentsCount;
    case 'debt':
      return g.debtTotal;
    case 'debtors':
      return g.debtorsCount;
    case 'unanswered':
      return g.unansweredCount;
    case 'tasks':
      return g.openTasksCount;
    default:
      return g.name.toLowerCase();
  }
};

interface IGroupsTableProps {
  groups: IGroupOverview[];
  loading: boolean;
  onlyIssues: boolean;
  onOnlyIssues: (value: boolean) => void;
}

/**
 * Dashboard groups roster (Группы tab): sortable table of per-group debt /
 * debtor / unanswered / task signals; a row click opens the group, the action
 * links deep-link into its communication and finances tabs.
 */
export const GroupsTable = memo(
  ({ groups, loading, onlyIssues, onOnlyIssues }: IGroupsTableProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
      key: 'debt',
      dir: 'desc',
    });

    const sorted = useMemo(() => {
      const arr = [...groups];
      arr.sort((a, b) => {
        const va = sortValue(a, sort.key);
        const vb = sortValue(b, sort.key);
        if (va < vb) return sort.dir === 'asc' ? -1 : 1;
        if (va > vb) return sort.dir === 'asc' ? 1 : -1;
        return 0;
      });
      return arr;
    }, [groups, sort]);

    const onSort = (key: SortKey) =>
      setSort((prev) =>
        prev.key === key
          ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
          : { key, dir: key === 'name' ? 'asc' : 'desc' },
      );

    const openGroup = (id: string, tab?: string) =>
      history.push(
        `${PATHS.groupDetail.replace(':id', id)}${tab ? `?tab=${tab}` : ''}`,
      );

    return (
      <>
        <S.TabToolbar>
          <S.Toggle>
            <input
              type="checkbox"
              checked={onlyIssues}
              onChange={(e) => onOnlyIssues(e.target.checked)}
            />
            {t('pages.dashboard.onlyIssues')}
          </S.Toggle>
        </S.TabToolbar>

        {loading && groups.length === 0 ? (
          <S.Muted>{t('pages.dashboard.loading')}</S.Muted>
        ) : groups.length === 0 ? (
          <S.Muted>{t('pages.dashboard.empty')}</S.Muted>
        ) : (
          <S.Table>
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
              <col style={{ width: '160px' }} />
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <S.Th
                    key={c.key}
                    align={c.align}
                    onClick={() => onSort(c.key)}
                  >
                    {t(`pages.dashboard.col.${c.label}`)}
                    {sort.key === c.key ? (
                      <S.SortArrow>
                        {sort.dir === 'asc' ? '▲' : '▼'}
                      </S.SortArrow>
                    ) : null}
                  </S.Th>
                ))}
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((g) => (
                <tr key={g.groupId} onClick={() => openGroup(g.groupId)}>
                  <S.GroupCell>
                    <b>{g.name}</b>
                    <span>{g.course}</span>
                  </S.GroupCell>
                  <S.Num>{g.studentsCount}</S.Num>
                  <S.Num danger={g.debtTotal > 0}>
                    {fmtDebt(g.debtTotal, g.debtCurrency)}
                  </S.Num>
                  <S.Num danger={g.debtorsCount > 0}>
                    {g.debtorsCount || '—'}
                  </S.Num>
                  <S.Num warn={g.unansweredCount > 0}>
                    {g.unansweredCount || '—'}
                  </S.Num>
                  <S.Num danger={g.overdueTasksCount > 0}>
                    {g.openTasksCount > 0
                      ? `${g.openTasksCount}${
                          g.overdueTasksCount > 0
                            ? ` / ${g.overdueTasksCount}!`
                            : ''
                        }`
                      : '—'}
                  </S.Num>
                  <S.ActionCell onClick={(e) => e.stopPropagation()}>
                    <S.LinkBtn
                      type="button"
                      onClick={() => openGroup(g.groupId, 'communication')}
                    >
                      {t('pages.dashboard.openComms')}
                    </S.LinkBtn>
                    <S.LinkBtn
                      type="button"
                      onClick={() => openGroup(g.groupId, 'finances')}
                    >
                      {t('pages.dashboard.openFinance')}
                    </S.LinkBtn>
                  </S.ActionCell>
                </tr>
              ))}
            </tbody>
          </S.Table>
        )}
      </>
    );
  },
);

GroupsTable.displayName = 'GroupsTable';
